import React, { useState, useEffect, useRef } from "react";
// Neutral grey placeholder (no running-man, no dead remote service).
import { GREY_PLACEHOLDER as defaultImage } from "../../constants/imageFallback";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getPitchesPendingContract,
  getPitchDocuments,
  getDocumentTemplates,
  getSignerSigningUrl,
  getPitchDocumentDownloadUrl,
} from "../../services/api";
import UploadTemplateModal from "../../components/common/UploadTemplateModal";
import CustomDropdown from "../../components/common/CustomDropdown";
import { useSelector } from "react-redux";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ROLES } from "../../config/roles";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

const TABS = [
  { id: "pending_contracts", label: "Pending Contracts" },
  { id: "pending_signatures", label: "Pending Signatures" },
  { id: "signed", label: "Signed" },
  { id: "all_contracts", label: "All contracts" }, // Renamed from 'templates'
];

const DocumentManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { currentRole } = useSelector((state) => state.auth);
  const isMobile = useMediaQuery("(max-width: 768px)");
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — route it through the same static-bundle lookup the
  // scanner uses before it goes to the native dialog.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;

  // Helper to get initial tab from URL or default
  const getInitialTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");
    if (tabFromUrl && TABS.find((t) => t.id === tabFromUrl)) {
      return tabFromUrl;
    }
    // If no valid tab in URL, navigate to default and return default
    // This navigation will cause a re-render, and this function will be called again
    // but by then location.search will have the default tab.
    // However, to avoid immediate navigation during render, we'll handle this in useEffect.
    return TABS[0].id;
  };

  const [activeTab, setActiveTab] = useState(getInitialTabFromUrl);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [pendingContracts, setPendingContracts] = useState([]);
  const [isLoadingPendingContracts, setIsLoadingPendingContracts] =
    useState(false);
  const [pendingContractsError, setPendingContractsError] = useState(null);

  const [pendingSignaturesData, setPendingSignaturesData] = useState([]);
  const [isLoadingPendingSignatures, setIsLoadingPendingSignatures] =
    useState(false);
  const [pendingSignaturesError, setPendingSignaturesError] = useState(null);

  const [signedDocumentsData, setSignedDocumentsData] = useState([]);
  const [isLoadingSignedDocuments, setIsLoadingSignedDocuments] =
    useState(false);
  const [signedDocumentsError, setSignedDocumentsError] = useState(null);

  const [allContractsData, setAllContractsData] = useState([]);
  const [isLoadingAllContracts, setIsLoadingAllContracts] = useState(false);
  const [allContractsError, setAllContractsError] = useState(null);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [docTypeFilter, setDocTypeFilter] = useState("player");

  // Effect to synchronize URL with activeTab and handle invalid/default tab in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentUrlTab = params.get("tab");
    const isValidUrlTab =
      currentUrlTab && TABS.find((t) => t.id === currentUrlTab);

    if (activeTab !== currentUrlTab) {
      navigate(`${location.pathname}?tab=${activeTab}`, { replace: true });
    } else if (!isValidUrlTab) {
      navigate(`${location.pathname}?tab=${activeTab}`, { replace: true });
    }
  }, [activeTab, location.pathname, location.search, navigate]);

  // Effect to update activeTab if URL changes externally (e.g., browser back/forward)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabQuery = params.get("tab");
    const targetTab =
      tabQuery && TABS.find((t) => t.id === tabQuery) ? tabQuery : TABS[0].id;
    if (activeTab !== targetTab) {
      setActiveTab(targetTab);
    }
  }, [location.search]);

  // The mobile tab strip brings the active tab into view — after a tap and on a
  // fresh load that arrives with ?tab=, where the selection can otherwise sit
  // entirely outside the 375px viewport. Same approach as the pitch strip in
  // components/viewpitches/ViewPitchesTabs.js:39-52: scrollTo with a 16px
  // lead-in and a scrollLeft fallback, deliberately NOT scrollIntoView, which
  // scrolls the page as well and yanks the list out from under the thumb.
  // The offset is taken from the two bounding rects rather than offsetLeft:
  // the pitch strip's pills sit inside a positioned wrapper that starts where
  // the strip starts, this one has no positioned ancestor, so offsetLeft would
  // carry the page offset with it.
  const tabStripRef = useRef(null);
  const tabRefs = useRef({});

  useEffect(() => {
    const strip = tabStripRef.current;
    const el = tabRefs.current[activeTab];
    if (!strip || !el) return;
    const target = Math.max(
      0,
      strip.scrollLeft +
        (el.getBoundingClientRect().left - strip.getBoundingClientRect().left) -
        16
    );
    if (typeof strip.scrollTo === "function") {
      try {
        strip.scrollTo({ left: target, behavior: "smooth" });
        return;
      } catch (err) {
        // Older engines reject the options object; fall through.
      }
    }
    strip.scrollLeft = target;
    // The counters arrive after the first paint and widen every tab, which
    // pushes the active one back out — so the strip re-aims once they land.
  }, [
    activeTab,
    isMobile,
    pendingContracts,
    pendingSignaturesData,
    signedDocumentsData,
    allContractsData,
  ]);

  // Data fetching logic based on activeTab
  useEffect(() => {
    // const commonParams = { search: searchQuery, sort_by: sortBy }; // Add sortBy state if implementing sorting
    // Pass searchQuery for server-side filtering if API supports it
    const commonParams = { search: searchQuery }; // Use if API supports search

    if (activeTab === "pending_contracts") {
      fetchPendingContracts(commonParams);
    } else if (activeTab === "pending_signatures") {
      fetchPendingSignatures(commonParams); // Assuming this API also supports search
    } else if (activeTab === "signed") {
      fetchSignedDocuments(commonParams); // Assuming this API also supports search
    } else if (activeTab === "all_contracts") {
      fetchAllContracts(commonParams); // Assuming this API also supports search
    }
    // Trigger fetch when activeTab or searchQuery changes
  }, [activeTab, searchQuery]); // Removed sortBy, add back if used with API

  const fetchPendingContracts = async (params) => {
    setIsLoadingPendingContracts(true);
    setPendingContractsError(null);
    try {
      // const apiParams = { ...params };
      // delete apiParams.sort_by; // remove sort_by if not used by API
      const response = await getPitchesPendingContract(params); // pass params directly
      setPendingContracts(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching pending contracts:", error);
      setPendingContractsError("Failed to load pending contracts.");
      setPendingContracts([]);
    } finally {
      setIsLoadingPendingContracts(false);
    }
  };

  const fetchPendingSignatures = async (params) => {
    setIsLoadingPendingSignatures(true);
    setPendingSignaturesError(null);
    try {
      // Fetch documents awaiting signatures (pending or partially signed)
      const apiParams = {
        ...params,
        status: "pending_signatures,partially_signed",
      };
      const response = await getPitchDocuments(apiParams);
      setPendingSignaturesData(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching pending signatures:", error);
      setPendingSignaturesError("Failed to load documents pending signature.");
      setPendingSignaturesData([]);
    } finally {
      setIsLoadingPendingSignatures(false);
    }
  };

  const fetchSignedDocuments = async (params) => {
    setIsLoadingSignedDocuments(true);
    setSignedDocumentsError(null);
    try {
      const apiParams = { ...params, status: "fully_signed" };
      const response = await getPitchDocuments(apiParams);
      setSignedDocumentsData(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching signed documents:", error);
      setSignedDocumentsError("Failed to load signed documents.");
      setSignedDocumentsData([]);
    } finally {
      setIsLoadingSignedDocuments(false);
    }
  };

  const fetchAllContracts = async (params) => {
    setIsLoadingAllContracts(true);
    setAllContractsError(null);
    try {
      // const apiParams = { ...params };
      // delete apiParams.sort_by; // remove sort_by if not used by API
      const response = await getDocumentTemplates(params); // pass params directly
      setAllContractsData(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching document templates:", error);
      setAllContractsError("Failed to load document templates.");
      setAllContractsData([]);
    } finally {
      setIsLoadingAllContracts(false);
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    navigate(`${location.pathname}?tab=${tabId}`, { replace: true });
  };

  const getStatusBadgeClasses = (statusLabel) => {
    if (statusLabel === "SIGNED BY ALL PARTIES") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (statusLabel && statusLabel.includes("YOU")) {
      return "bg-rose-100 text-rose-700";
    }
    if (statusLabel) {
      // For 'PENDING TO SIGN BY OTHER PARTIES'
      return "bg-amber-100 text-amber-700";
    }
    return "bg-gray-100 text-gray-700"; // Default or for Draft
  };

  const getTypeBadgeClasses = (type) => {
    switch (type?.toLowerCase()) {
      case "buy":
        return "bg-green-100 text-green-700";
      case "loan":
        return "bg-blue-100 text-blue-700";
      case "staff":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleSignNow = async (doc) => {
    if (!currentUser || !doc.signers) {
      alert(tr("User information or signer details missing."));
      return;
    }
    // Find the signer ID for the current user
    // This assumes doc.signers is an array of objects like { user_id: X, id: Y (this is DocumentSignature.id) }
    // Or { email: 'user@example.com', id: Y }
    // Adjust the logic based on your actual API response for PitchDocumentSerializer and its signers field.
    const currentUserSignerInfo = doc.signers.find(
      (signer) =>
        (signer.user_id && signer.user_id === currentUser.id) ||
        (signer.email &&
          currentUser.email &&
          signer.email.toLowerCase() === currentUser.email.toLowerCase())
    );

    if (currentUserSignerInfo && currentUserSignerInfo.id) {
      // Assuming currentUserSignerInfo.id is the DocumentSignature record ID
      try {
        setIsLoadingPendingSignatures(true); // Or a more specific loading state
        const response = await getSignerSigningUrl(
          doc.id,
          currentUserSignerInfo.id
        );
        if (response.data?.signing_url) {
          window.open(response.data.signing_url, "_blank"); // Open in new tab
        } else {
          alert(
            "Could not retrieve signing URL. " +
              (response.error || "Please try again.")
          );
        }
      } catch (error) {
        console.error("Error getting signing URL:", error);
        alert(tr("An error occurred while trying to get the signing URL."));
      } finally {
        setIsLoadingPendingSignatures(false);
      }
    } else {
      alert(
        "You are not a designated signer for this document, or signer ID could not be found."
      );
    }
  };

  const renderPendingContracts = () => {
    if (isLoadingPendingContracts) {
      return <p className="text-center py-8">Loading pending contracts...</p>;
    }
    if (pendingContractsError) {
      return (
        <p className="text-center py-8 text-red-600">{pendingContractsError}</p>
      );
    }

    // Only keep contracts that are still in DRAFT status
    const draftContracts = pendingContracts.filter(
      (c) => c.has_documents && c.document?.status === "draft"
    );

    const filteredContracts = draftContracts.filter(
      (c) =>
        (c.player?.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (c.tender_position || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (c.applied_by_agent?.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
    );

    if (filteredContracts.length === 0) {
      return (
        <p className="text-center py-8 text-gray-500">
          {searchQuery
            ? "No pending contracts match your search."
            : "No pending contracts found."}
        </p>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-sticky-first">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Position
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Player
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Agency
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredContracts.map((contract) => {
              const isDraft = true; // By definition of draftContracts list
              return (
                <tr key={contract.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.tender_position || "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadgeClasses(
                        contract.tender_type
                      )}`}
                    >
                      {contract.tender_type?.toUpperCase() || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <img
                          className="h-8 w-8 rounded-full object-cover"
                          src={
                            contract.player?.image ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              contract.player?.name || "P"
                            )}&background=random`
                          }
                          alt={contract.player?.name || "Player"}
                        />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {contract.player?.name || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        {/* Placeholder for agency logo - assuming applied_by_agent is the agency */}
                        <img
                          className="h-8 w-8 rounded-full object-contain bg-gray-200 p-1"
                          src={
                            contract.applied_by_agent?.logo ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              contract.applied_by_agent?.name || "A"
                            )}&background=random`
                          }
                          alt={contract.applied_by_agent?.name || "Agency"}
                        />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {contract.applied_by_agent?.name || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isDraft ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Draft
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        Pending Creation
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {isDraft ? (
                      <button
                        onClick={() => {
                          // Ensure all necessary data is passed for resume
                          const resumeState = {
                            resumeMode: true,
                            pitchDocumentIdToResume: contract.document?.id,
                            dealToResume: {
                              // Reconstruct deal object for CreateContract
                              id: contract.id,
                              tender_position: contract.tender_position,
                              tender_type: contract.tender_type,
                              player: contract.player,
                              applied_by_agent: contract.applied_by_agent, // or agency: contract.agency
                              created_by: contract.created_by, // pass created_by if needed for signers
                              // Add other fields of the 'deal' that CreateContract might expect
                            },
                            // Pass template and signer info if available and needed by CreateContract for resume
                            templateIdToResume:
                              contract.document?.template
                                ?.docuseal_template_id ||
                              contract.document?.docuseal_template_id, // Adjust path as per actual API
                            signersToResume: contract.document?.signers, // Adjust path as per actual API
                            documentTitleToResume:
                              contract.document?.document_title,
                          };
                          console.log(
                            "Resuming contract with state:",
                            resumeState
                          );
                          navigate("/documents/create-contract", {
                            state: resumeState,
                          });
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Resume contract
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          navigate("/documents/create-contract", {
                            state: { dealId: contract.id },
                          })
                        }
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Create contract
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPendingSignatures = () => {
    if (isLoadingPendingSignatures) {
      return <p>Loading documents pending signature...</p>;
    }
    if (pendingSignaturesError) {
      return <p>{pendingSignaturesError}</p>;
    }
    if (pendingSignaturesData.length === 0) {
      return <p>No documents are currently pending signature.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pendingSignaturesData
          .filter((doc) =>
            docTypeFilter === "staff"
              ? doc.tender_category === "staff"
              : doc.tender_category !== "staff"
          )
          .filter(
            (doc) =>
              (doc.document_name || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              (doc.player_name || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              (doc.staff_name || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
          )
          .map((doc) => {
            const isCurrentUserPending = doc.signers?.some(
              (s) =>
                ((s.user_id && s.user_id === currentUser?.id) ||
                  (s.email &&
                    currentUser?.email &&
                    s.email.toLowerCase() ===
                      currentUser.email.toLowerCase())) &&
                s.status === "pending" // Assuming signer has a status field
            );
            const isStaff = doc.tender_category === "staff";
            const typeValue = isStaff ? doc.tender_type : doc.template_type;
            const positionValue = isStaff
              ? doc.tender_position || doc.player_position
              : doc.player_position;
            const personLabel = isStaff ? "Staff" : "Player";
            const personName = isStaff
              ? doc.staff_name || "N/A"
              : doc.player_name || "N/A";
            const personImage = isStaff
              ? doc.staff_image || defaultImage
              : doc.player_image || defaultImage;
            return (
              <div
                key={doc.id}
                className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(
                        doc.status_label || doc.status
                      )}`}
                    >
                      {/* Assuming status_label or status field from API response */}
                      {doc.status_label ||
                        doc.status?.replace("_", " ").toUpperCase() ||
                        "N/A"}
                    </span>
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        ></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-md font-semibold text-gray-900">
                        {doc.document_name || "Untitled Document"}
                      </h3>
                      <span
                        className={`mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadgeClasses(
                          typeValue
                        )}`}
                      >
                        {typeValue || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Position:</span>
                      <span className="text-gray-800 font-medium">
                        {positionValue || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">{personLabel}:</span>
                      <div className="flex items-center space-x-2">
                        <img
                          src={personImage}
                          alt={personName}
                          className="w-5 h-5 rounded-full"
                        />
                        <span className="text-gray-800 font-medium">
                          {personName}
                        </span>
                      </div>
                    </div>
                    {!isStaff && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Agent:</span>
                        <div className="flex items-center space-x-2">
                          <img
                            src={
                              doc.agency_image ||
                              defaultImage
                            }
                            alt={doc.agent_name}
                            className="w-5 h-5 rounded-full"
                          />
                          <span className="text-gray-800 font-medium">
                            {doc.agent_name || "N/A"}
                          </span>
                        </div>
                      </div>
                    )}
                    <hr className="my-2" />
                    {/* API field: document_signatures (array) from PitchDocumentSerializer */}
                    {doc.document_signatures?.map((signer, index) => (
                      <div
                        key={signer.id || index}
                        className="flex justify-between"
                      >
                        <span className="text-gray-500">
                          {signer.role_placeholder ||
                            `${index + 1}${
                              index === 0
                                ? "st"
                                : index === 1
                                ? "nd"
                                : index === 2
                                ? "rd"
                                : "th"
                            } Signer`}
                          :
                        </span>
                        {/* API field: signer.status or derived from signed_at */}
                        <span
                          className={`${
                            signer.signed_at ? "text-green-600" : "text-red-600"
                          } font-medium`}
                        >
                          {signer.name || signer.email} (
                          {signer.signed_at ? "Signed" : "Pending"})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* TODO: Logic for 'Sign now' button based on API data (e.g., if current user needs to sign) */}
                {/* For now, conditionally rendering based on a placeholder canSign logic */}
                {/* This needs to check if the current user is among the pending signers */}
                {isCurrentUserPending && (
                  <button
                    onClick={() => handleSignNow(doc)}
                    className="mt-4 w-full bg-rose-500 text-white py-2 px-4 rounded-md hover:bg-rose-600 transition-colors text-sm font-medium"
                  >
                    Sign Now
                  </button>
                )}
              </div>
            );
          })}
        {/* TODO: Adapt filtering if searchQuery is global or per tab */}
        {pendingSignaturesData.length > 0 &&
          pendingSignaturesData.filter((doc) =>
            (doc.document_name || "Untitled Document")
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          ).length === 0 &&
          searchQuery && (
            <p className="text-center text-gray-500 py-8 col-span-full">
              No pending signatures match your search.
            </p>
          )}
      </div>
    );
  };

  const renderSignedDocuments = () => {
    if (isLoadingSignedDocuments) {
      return <p>Loading signed documents...</p>;
    }
    if (signedDocumentsError) {
      return <p>{signedDocumentsError}</p>;
    }
    if (signedDocumentsData.length === 0) {
      return <p>No documents have been signed yet.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {signedDocumentsData
          .filter((doc) =>
            docTypeFilter === "staff"
              ? doc.tender_category === "staff"
              : doc.tender_category !== "staff"
          )
          .map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
            >
              <div className="flex items-start justify-between mb-3">
                <span
                  className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(
                    doc.status_label || "SIGNED BY ALL PARTIES"
                  )}`}
                >
                  {/* Assuming status_label or derive from status='fully_signed' */}
                  {doc.status_label || "SIGNED BY ALL PARTIES"}
                </span>
                <button
                  className="p-1 text-gray-400 hover:text-gray-600"
                  aria-label="Download document"
                  title="Download"
                  onClick={async () => {
                    try {
                      const res = await getPitchDocumentDownloadUrl(doc.id);
                      const url = res.data?.download_url;
                      if (url) {
                        window.open(url, "_blank");
                      } else {
                        alert(
                          res.data?.message ||
                            "Document is not ready for download."
                        );
                      }
                    } catch (err) {
                      const msg =
                        err?.response?.data?.message ||
                        "Failed to fetch signed document.";
                      alert(msg);
                    }
                  }}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2.5 bg-blue-50 rounded-lg">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-md font-semibold text-gray-900">
                    {doc.document_title ||
                      doc.document_name ||
                      doc.name ||
                      "Untitled Document"}
                  </h3>
                  {(() => {
                    const typeValue =
                      doc.tender_type || doc.pitch_type || doc.template_type;
                    return (
                      <span
                        className={`mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadgeClasses(
                          typeValue
                        )}`}
                      >
                        {typeValue || "N/A"}
                      </span>
                    );
                  })()}
                </div>
              </div>

              <div className="space-y-3 text-sm">
                {(() => {
                  const isStaff = doc.tender_category === "staff";
                  const positionValue = isStaff
                    ? doc.tender_position || doc.player_position
                    : doc.player_position;
                  const personLabel = isStaff ? "Staff" : "Player";
                  const personName = isStaff
                    ? doc.staff_name || "N/A"
                    : doc.player_name || "N/A";
                  const personImage = isStaff
                    ? doc.staff_image || defaultImage
                    : doc.player_image || defaultImage;
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Position:</span>
                        <span className="text-gray-800 font-medium">
                          {positionValue || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">{personLabel}:</span>
                        <div className="flex items-center space-x-2">
                          <img
                            src={personImage}
                            alt={personName}
                            className="w-5 h-5 rounded-full"
                          />
                          <span className="text-gray-800 font-medium">
                            {personName}
                          </span>
                        </div>
                      </div>
                    </>
                  );
                })()}
                {doc.tender_category !== "staff" && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Agent:</span>
                    <div className="flex items-center space-x-2">
                      <img
                        src={
                          doc.agency_image ||
                          defaultImage
                        }
                        alt={doc.agent_name}
                        className="w-5 h-5 rounded-full"
                      />
                      <span className="text-gray-800 font-medium">
                        {doc.agent_name || "N/A"}
                      </span>
                    </div>
                  </div>
                )}
                <hr className="my-2" />
                {doc.document_signatures?.map((signer, index) => (
                  <div
                    key={signer.id || index}
                    className="flex justify-between"
                  >
                    <span className="text-gray-500">
                      {signer.role_placeholder ||
                        `${index + 1}${
                          index === 0
                            ? "st"
                            : index === 1
                            ? "nd"
                            : index === 2
                            ? "rd"
                            : "th"
                        } Signer`}
                      :
                    </span>
                    <span className="text-green-600 font-medium">
                      {signer.name || signer.email} (Signed)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        {signedDocumentsData.length > 0 &&
          signedDocumentsData.filter((doc) =>
            (doc.document_title || doc.name || "Untitled Document")
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          ).length === 0 &&
          searchQuery && (
            <p className="text-center text-gray-500 py-8 col-span-full">
              No signed documents match your search.
            </p>
          )}
      </div>
    );
  };

  const renderAllContracts = () => {
    if (isLoadingAllContracts) {
      return <p>Loading document templates...</p>;
    }
    if (allContractsError) {
      return <p>{allContractsError}</p>;
    }
    if (allContractsData.length === 0 && !searchQuery) {
      return (
        <p className="text-center text-gray-500 py-8">
          No templates available.{" "}
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="text-blue-600 hover:underline"
          >
            Upload your first template.
          </button>
        </p>
      );
    }

    const filteredTemplates = allContractsData.filter(
      (template) =>
        (template.name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (template.template_type || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
    );

    if (filteredTemplates.length === 0 && searchQuery) {
      return (
        <p className="text-center text-gray-500 py-8">
          No templates match your search.
        </p>
      );
    }
    if (
      filteredTemplates.length === 0 &&
      !searchQuery &&
      allContractsData.length > 0
    ) {
      // This case might not be hit if the initial check for allContractsData.length === 0 is true
      return (
        <p className="text-center text-gray-500 py-8">
          No templates match your search.
        </p>
      );
    }
    if (
      filteredTemplates.length === 0 &&
      !searchQuery &&
      allContractsData.length === 0
    ) {
      return (
        <p className="text-center text-gray-500 py-8">
          No templates available.{" "}
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="text-blue-600 hover:underline"
          >
            Upload your first template.
          </button>
        </p>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm flex flex-col"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2.5 bg-indigo-50 rounded-lg">
                <svg
                  className="w-6 h-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
              </div>
              {/* API field: name from DocumentTemplateSerializer */}
              <h3 className="text-md font-semibold text-gray-800 truncate flex-1">
                {template.name || "Untitled Template"}
              </h3>
              <button className="p-1 text-gray-400 hover:text-gray-600">
                {/* TODO: Implement three-dot menu: Use, Edit, Delete */}
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>

            <div className="space-y-2 text-sm mb-4 flex-grow">
              {/* API field: template_type from DocumentTemplateSerializer */}
              <div className="flex justify-between">
                <span className="text-gray-500">Contract type:</span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadgeClasses(
                    template.template_type
                  )}`}
                >
                  {template.template_type?.replace("_", " ").toUpperCase() ||
                    "N/A"}
                </span>
              </div>
              {/* API field: player_position from DocumentTemplateSerializer */}
              <div className="flex justify-between">
                <span className="text-gray-500">Player Position:</span>
                <span className="text-gray-700 font-medium">
                  {template.player_position || "N/A"}
                </span>
              </div>
            </div>
            <button
              onClick={() =>
                navigate("/documents/create-contract", {
                  state: { templateId: template.id },
                })
              }
              className="w-full mt-auto bg-indigo-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Use Template
            </button>
          </div>
        ))}
      </div>
    );
  };

  const handleUploadSuccess = () => {
    fetchAllContracts();
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-base md:text-xl font-bold text-[#222A35]">
              Documents manager
            </h1>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/documents/edit-signature")}
                className="px-4 py-2 rounded-lg bg-white border border-[#E6E6E6] flex gap-2 items-center justify-center cursor-pointer"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.334 2.00004C11.5091 1.82494 11.7169 1.68605 11.9457 1.59129C12.1745 1.49653 12.4197 1.44775 12.6673 1.44775C12.9149 1.44775 13.1601 1.49653 13.3889 1.59129C13.6177 1.68605 13.8256 1.82494 14.0007 2.00004C14.1757 2.17513 14.3146 2.383 14.4094 2.61178C14.5042 2.84055 14.5529 3.08575 14.5529 3.33337C14.5529 3.58099 14.5042 3.82619 14.4094 4.05497C14.3146 4.28374 14.1757 4.49161 14.0007 4.66671L5.00065 13.6667L1.33398 14.6667L2.33398 11L11.334 2.00004Z"
                    stroke="#2C2C2C"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                {!isMobile && (
                  <span className="text-[#1E1E1E]">Edit signature</span>
                )}
              </button>
              {activeTab === "all_contracts" ? (
                <button
                  onClick={() => setIsUploadModalOpen(true)}
                  className="px-4 py-2 rounded-lg cursor-pointer bg-[#2283FF] text-[#F5F5F5]"
                >
                  {isMobile ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.6673 10V12.6667C12.6673 13.0203 12.5268 13.3594 12.2766 13.6096C12.0264 13.8598 11.6873 14 11.3337 14H4.66699C4.31336 14 3.97428 13.8598 3.72407 13.6096C3.47386 13.3594 3.33333 13.0203 3.33333 12.6667V10"
                        stroke="white"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M10.666 5.33331L7.99935 2.66665L5.33268 5.33331"
                        stroke="white"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M8 2.66665V10"
                        stroke="white"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  ) : (
                    "Upload template"
                  )}
                </button>
              ) : (
                currentRole === ROLES.ClubAdmin && (
                  <button
                    onClick={() => navigate("/documents/create-contract")}
                    className="px-4 py-2 rounded-lg cursor-pointer bg-[#2283FF] text-[#F5F5F5]"
                  >
                    {isMobile ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 3.33331V12.6666"
                          stroke="white"
                          stroke-width="1.6"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M3.33398 8H12.6673"
                          stroke="white"
                          stroke-width="1.6"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    ) : (
                      "Create new contract"
                    )}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sub Header with Tabs and Search */}
      {isMobile ? (
        <>
          <div
            ref={tabStripRef}
            className="px-4 bg-white flex justify-between items-center border-y border-[#0000001A] overflow-x-auto"
          >
            <div className="flex items-center gap-4">
              <div
                ref={(el) => (tabRefs.current.pending_contracts = el)}
                onClick={() => handleTabClick("pending_contracts")}
                className={`py-4 text-xs whitespace-nowrap cursor-pointer ${
                  activeTab === "pending_contracts"
                    ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                    : "text-[#1E1E1ECC]"
                }`}
              >
                Pending Contracts{" "}
                {isLoadingPendingContracts
                  ? ""
                  : `(${
                      pendingContracts.filter(
                        (c) => c.has_documents && c.document?.status === "draft"
                      ).length
                    })`}
              </div>
              <div
                ref={(el) => (tabRefs.current.pending_signatures = el)}
                onClick={() => handleTabClick("pending_signatures")}
                className={`py-4 text-xs whitespace-nowrap cursor-pointer ${
                  activeTab === "pending_signatures"
                    ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                    : "text-[#1E1E1ECC]"
                }`}
              >
                Pending Signatures{" "}
                {isLoadingPendingSignatures
                  ? ""
                  : `(${pendingSignaturesData.length})`}
              </div>
              <div
                ref={(el) => (tabRefs.current.signed = el)}
                onClick={() => handleTabClick("signed")}
                className={`py-4 text-xs whitespace-nowrap cursor-pointer ${
                  activeTab === "signed"
                    ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                    : "text-[#1E1E1ECC]"
                }`}
              >
                Signed {isLoadingSignedDocuments
                  ? ""
                  : `(${signedDocumentsData.length})`}
              </div>
              <div
                ref={(el) => (tabRefs.current.all_contracts = el)}
                onClick={() => handleTabClick("all_contracts")}
                className={`py-4 text-xs whitespace-nowrap cursor-pointer ${
                  activeTab === "all_contracts"
                    ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                    : "text-[#1E1E1ECC]"
                }`}
              >
                All contracts{" "}
                {isLoadingAllContracts ? "" : `(${allContractsData.length})`}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-white flex justify-end items-center border-b border-[#0000001A]">
            <div className="w-[160px]">
              <CustomDropdown
                options={[
                  { label: "Player", value: "player" },
                  { label: "Staff", value: "staff" },
                ]}
                value={docTypeFilter}
                onChange={setDocTypeFilter}
                placeholder="Select type"
                paddingY="py-2"
              />
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-12 py-4">
            <div className="flex items-center justify-between ">
              <div className="flex space-x-6">
                {/* Updated Tabs */}
                <button
                  onClick={() => handleTabClick("pending_contracts")}
                  className={`px-4 py-2 rounded-lg cursor-pointer ${
                    activeTab === "pending_contracts"
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC]"
                  }`}
                >
                  Pending Contracts{" "}
                  {isLoadingPendingContracts
                    ? ""
                    : `(${
                        pendingContracts.filter(
                          (c) =>
                            c.has_documents && c.document?.status === "draft"
                        ).length
                      })`}
                </button>
                <button
                  onClick={() => handleTabClick("pending_signatures")}
                  className={`px-4 py-2 rounded-lg cursor-pointer ${
                    activeTab === "pending_signatures"
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC]"
                  }`}
                >
                  Pending Signatures{" "}
                  {isLoadingPendingSignatures
                    ? ""
                    : `(${pendingSignaturesData.length})`}
                </button>
                <button
                  onClick={() => handleTabClick("signed")}
                  className={`px-4 py-2 rounded-lg cursor-pointer ${
                    activeTab === "signed"
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC]"
                  }`}
                >
                  Signed{" "}
                  {isLoadingSignedDocuments
                    ? ""
                    : `(${signedDocumentsData.length})`}
                </button>
                <button
                  onClick={() => handleTabClick("all_contracts")}
                  className={`px-4 py-2 rounded-lg cursor-pointer ${
                    activeTab === "all_contracts"
                      ? "bg-[#F3F4F6] text-[#0B0B0B]"
                      : "text-[#1E1E1ECC]"
                  }`}
                >
                  All contracts{" "}
                  {isLoadingAllContracts ? "" : `(${allContractsData.length})`}
                </button>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-[160px]">
                  <CustomDropdown
                    options={[
                      { label: "Player", value: "player" },
                      { label: "Staff", value: "staff" },
                    ]}
                    value={docTypeFilter}
                    onChange={setDocTypeFilter}
                    placeholder="Select type"
                    paddingY="py-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document List */}
      <div className="px-4 md:px-12 py-8">
        {activeTab === "pending_contracts" && renderPendingContracts()}
        {activeTab === "pending_signatures" && renderPendingSignatures()}
        {activeTab === "signed" && renderSignedDocuments()}
        {activeTab === "all_contracts" && renderAllContracts()}
      </div>

      <UploadTemplateModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default DocumentManager;
